import {
  MdCheck,
  MdCheckCircleOutline,
  MdClose,
  MdDoneAll,
  MdErrorOutline,
  MdInfo,
  MdOutlinePriorityHigh,
  MdOutlineRule,
} from "react-icons/md";
import { ICON_SIZES } from "../../helpers/constants";
import clsx from "clsx";
import { useEffect, useState } from "react";
import useMobile from "../../hooks/useMobile";
import InfoBlock from "../InfoBlock/InfoBlock";
import Modal from "../Modal/Modal";

interface IField {
  name: string;
  completed: boolean;
}

type FieldsValidationType = {
  fieldsToValidate: Array<IField>;
  onChange: (allCompleted: boolean) => void;
  showFields?: boolean;
  showButton?: boolean;
  message?: string;
};

const FieldsValidation: React.FC<FieldsValidationType> = ({
  fieldsToValidate,
  onChange,
  showFields = true,
  showButton = false,
  message = null,
}) => {
  const { isMobile } = useMobile();
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const pendingToComplete = fieldsToValidate?.some(
    (field: IField) => !field?.completed
  );

  useEffect(() => {
    onChange(!pendingToComplete);
  }, [pendingToComplete, onChange]);

  return (
    <>
      <InfoBlock
        title={pendingToComplete ? "Campos incompletos" : "Campos completos"}
        displayMobileIcon
        icon={
          pendingToComplete ? (
            !showFields ? (
              <MdOutlineRule size={ICON_SIZES.MD} />
            ) : null
          ) : (
            <MdDoneAll size={ICON_SIZES.MD} />
          )
        }
        message={
          pendingToComplete ? (
            <div className={clsx(["flex", "flex-col", "gap-2", "md:gap-2"])}>
              {pendingToComplete && (
                <div className={clsx(["text-sm", "md:text-sm", "w-full"])}>
                  Debes completar todos los campos requeridos.
                </div>
              )}
              {showFields && (
                <ul
                  className={clsx([
                    "flex",
                    "flex-row",
                    "gap-2",
                    "md:gap-4",
                    "md:text-sm",
                    "text-sm",
                    "flex-wrap",
                  ])}
                >
                  {fieldsToValidate.map(
                    ({ name, completed }: IField, index) => (
                      <li
                        key={index}
                        className={clsx([
                          "flex",
                          "flex-row",
                          "items-center",
                          "gap-1",
                        ])}
                      >
                        {completed ? (
                          <MdCheckCircleOutline
                            size={isMobile ? ICON_SIZES.XS : ICON_SIZES.MD}
                            className={clsx(["text-lime-500"])}
                          />
                        ) : (
                          <MdErrorOutline
                            size={isMobile ? ICON_SIZES.XS : ICON_SIZES.MD}
                            className={clsx(["text-red-400"])}
                          />
                        )}
                        <div className={clsx(["text-sm", "md:text-sm"])}>
                          {name}
                        </div>
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
          ) : (
            "Todos los campos requeridos están completos."
          )
        }
        buttons={[
          {
            tooltip: "Ver campos",
            icon: <MdInfo size={ICON_SIZES.MD} />,
            onClick: () => setShowStatusModal(true),
            hide: !showButton,
          },
        ]}
      />
      {showStatusModal && (
        <Modal
          onEscape={() => setShowStatusModal(false)}
          className="md:!max-w-[550px]"
        >
          <Modal.Header
            onClose={() => setShowStatusModal(false)}
            title="Campos requeridos"
          />
          <Modal.Body>
            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-col",
                "gap-4",
                "md:gap-4",
                "pb-2",
              ])}
            >
              {message && (
                <div className={clsx(["text-sm", "md:text-base"])}>
                  {message}
                </div>
              )}
              <ul
                className={clsx([
                  "grid",
                  "w-full",
                  "grid-cols-2",
                  "md:grid-cols-2",
                  "gap-2",
                  "md:gap-2",
                  "md:text-sm",
                  "text-sm",
                ])}
              >
                {fieldsToValidate.map(({ name, completed }: IField, index) => (
                  <li
                    key={index}
                    className={clsx([
                      "flex",
                      "flex-row",
                      "items-center",
                      "gap-1",
                    ])}
                  >
                    {completed ? (
                      <MdCheck
                        size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                        className={clsx(["text-lime-400"])}
                      />
                    ) : (
                      <MdOutlinePriorityHigh
                        size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                        className={clsx(["text-red-400 dark:text-rose-400"])}
                      />
                    )}
                    <div className={clsx(["text-sm", "md:text-sm"])}>
                      {name}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer
            leftContent={
              pendingToComplete ? null : (
                <div className="text-sm">Todos los campos están completos</div>
              )
            }
            actions={[
              {
                text: "Cerrar",
                onClick: () => setShowStatusModal(false),
                icon: <MdClose size={ICON_SIZES.MD} />,
              },
            ]}
          />
        </Modal>
      )}
    </>
  );
};

export default FieldsValidation;
